import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Calendar, MapPin, Clock, DollarSign,
  CheckCircle, AlertTriangle, Download, FileText, MessageCircle,
  Eye, ThumbsUp, ThumbsDown, MoreVertical, Shield, Star
} from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';

const RFQDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSupabase();
  const [rfq, setRfq] = useState<any>(null);
  const [bids, setBids] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBid, setSelectedBid] = useState<string | null>(null);
  
  // Check if user is logged in
  useEffect(() => {
    if (!user) {
      navigate('/signin?redirect=rfq/' + id);
    }
  }, [user, navigate, id]);
  
  // Fetch RFQ details
  useEffect(() => {
    const fetchRFQDetails = async () => {
      if (!user || !id) return;
      
      try {
        setLoading(true);
        
        // Fetch RFQ
        const { data: rfqData, error: rfqError } = await supabase
          .from('rfq_requests')
          .select(`
            *,
            buyer_request_categories (
              category_id,
              categories:category_id (name)
            ),
            buyers:buyer_id (
              first_name,
              last_name,
              email,
              country
            )
          `)
          .eq('id', id)
          .single();
        
        if (rfqError) throw rfqError;
        
        // Check if user is the buyer
        if (rfqData.buyer_id !== user.id) {
          // Check if user is a supplier
          const { data: supplierData } = await supabase
            .from('suppliers')
            .select('id')
            .eq('user_id', user.id)
            .single();
          
          if (!supplierData) {
            throw new Error('You do not have permission to view this RFQ');
          }
        }
        
        setRfq(rfqData);
        
        // Fetch bids
        const { data: bidsData, error: bidsError } = await supabase
          .from('supplier_bids')
          .select(`
            *,
            suppliers (
              id,
              organization,
              representative,
              location,
              verified,
              rating,
              year_established
            )
          `)
          .eq('rfq_id', id)
          .order('created_at', { ascending: false });
        
        if (bidsError) throw bidsError;
        
        setBids(bidsData || []);
        
      } catch (err) {
        console.error('Error fetching RFQ details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load RFQ details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRFQDetails();
  }, [user, id]);
  
  // Accept bid
  const handleAcceptBid = async (bidId: string) => {
    if (!confirm('Are you sure you want to accept this quote? This will close the RFQ.')) return;
    
    try {
      // Update bid status
      const { error: bidError } = await supabase
        .from('supplier_bids')
        .update({ status: 'accepted' })
        .eq('id', bidId);
      
      if (bidError) throw bidError;
      
      // Update other bids to rejected
      const { error: otherBidsError } = await supabase
        .from('supplier_bids')
        .update({ status: 'rejected' })
        .eq('rfq_id', id)
        .neq('id', bidId);
      
      if (otherBidsError) throw otherBidsError;
      
      // Update RFQ status
      const { error: rfqError } = await supabase
        .from('rfq_requests')
        .update({ status: 'in_progress' })
        .eq('id', id);
      
      if (rfqError) throw rfqError;
      
      // Update local state
      setRfq({ ...rfq, status: 'in_progress' });
      setBids(bids.map(bid => ({
        ...bid,
        status: bid.id === bidId ? 'accepted' : 'rejected'
      })));
      
      setSelectedBid(bidId);
      
    } catch (err) {
      console.error('Error accepting bid:', err);
      alert('Failed to accept quote. Please try again.');
    }
  };
  
  // Reject bid
  const handleRejectBid = async (bidId: string) => {
    if (!confirm('Are you sure you want to reject this quote?')) return;
    
    try {
      // Update bid status
      const { error } = await supabase
        .from('supplier_bids')
        .update({ status: 'rejected' })
        .eq('id', bidId);
      
      if (error) throw error;
      
      // Update local state
      setBids(bids.map(bid => 
        bid.id === bidId ? { ...bid, status: 'rejected' } : bid
      ));
      
    } catch (err) {
      console.error('Error rejecting bid:', err);
      alert('Failed to reject quote. Please try again.');
    }
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Open
        </span>;
      case 'in_progress':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Clock className="h-3 w-3 mr-1" />
          In Progress
        </span>;
      case 'fulfilled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Fulfilled
        </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Cancelled
        </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Expired
        </span>;
      default:
        return null;
    }
  };
  
  // Get bid status badge
  const getBidStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'accepted':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Accepted
        </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ThumbsDown className="h-3 w-3 mr-1" />
          Rejected
        </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Expired
        </span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Error</h2>
          <p className="text-gray-600 mb-6 text-center">{error}</p>
          <div className="flex justify-center">
            <Link 
              to="/my-rfqs" 
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            >
              Back to My RFQs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">RFQ Not Found</h2>
          <p className="text-gray-600 mb-6 text-center">The requested RFQ could not be found or you don't have permission to view it.</p>
          <div className="flex justify-center">
            <Link 
              to="/my-rfqs" 
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            >
              Back to My RFQs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Link to="/my-rfqs" className="mr-4 text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-gray-900">RFQ Details</h1>
                {getStatusBadge(rfq.status)}
              </div>
              <p className="text-gray-600">View details and quotes for your request</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* RFQ Details */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">{rfq.title}</h2>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {rfq.buyer_request_categories?.map((category: any, index: number) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {category.categories?.name}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end">
                  <div className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Created:</span> {formatDate(rfq.created_at)}
                  </div>
                  <div className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Expires:</span> {formatDate(rfq.expiry_date)}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Views:</span> {rfq.view_count}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Package className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Quantity</span>
                  </div>
                  <p className="text-gray-700">{rfq.quantity_required} units</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Budget</span>
                  </div>
                  <p className="text-gray-700">
                    {rfq.budget_min && rfq.budget_max
                      ? `$${rfq.budget_min} - $${rfq.budget_max} per unit`
                      : 'Not specified'}
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Shipping To</span>
                  </div>
                  <p className="text-gray-700">{rfq.shipping_country}</p>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-line">{rfq.description}</p>
              </div>
              
              {rfq.requirements && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">Additional Requirements</h3>
                  <p className="text-gray-700 whitespace-pre-line">{rfq.requirements}</p>
                </div>
              )}
              
              {rfq.attachments && rfq.attachments.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Attachments</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {rfq.attachments.map((attachment: string, index: number) => (
                      <a 
                        key={index}
                        href={attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <FileText className="h-5 w-5 text-orange-600" />
                        <span className="text-sm text-gray-700 truncate flex-1">Attachment {index + 1}</span>
                        <Download className="h-4 w-4 text-gray-500" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quotes Received */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Quotes Received ({bids.length})
              </h2>
              
              {bids.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Quotes Yet</h3>
                  <p className="text-gray-600 mb-4">
                    {rfq.status === 'open'
                      ? 'Suppliers are reviewing your request. You will be notified when quotes are received.'
                      : 'No quotes were received for this RFQ.'}
                  </p>
                  {rfq.status === 'open' && (
                    <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors">
                      Invite Suppliers
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {bids.map((bid) => (
                    <div 
                      key={bid.id} 
                      className={`border rounded-lg overflow-hidden ${
                        bid.status === 'accepted' 
                          ? 'border-green-300 bg-green-50' 
                          : bid.status === 'rejected'
                          ? 'border-gray-200 bg-gray-50 opacity-75'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                          <div>
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-gray-900">{bid.suppliers?.organization}</h3>
                              {bid.suppliers?.verified && (
                                <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                                  <Shield className="h-3 w-3" />
                                  <span>Verified</span>
                                </div>
                              )}
                              {getBidStatusBadge(bid.status)}
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              <span className="inline-flex items-center mr-4">
                                <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                                {bid.suppliers?.location}
                              </span>
                              {bid.suppliers?.year_established && (
                                <span className="inline-flex items-center mr-4">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                  Est. {bid.suppliers?.year_established}
                                </span>
                              )}
                              {bid.suppliers?.rating && (
                                <span className="inline-flex items-center">
                                  <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                                  {bid.suppliers?.rating}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="mt-4 md:mt-0">
                            <div className="text-xl font-bold text-gray-900 mb-1">${bid.price} <span className="text-sm font-normal">per unit</span></div>
                            <div className="text-sm text-gray-600 mb-2">
                              Total: ${(bid.price * bid.quantity).toFixed(2)} for {bid.quantity} units
                            </div>
                            <div className="text-sm text-gray-600">
                              Delivery Time: {bid.delivery_time}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <h4 className="font-medium text-gray-900 mb-2">Quote Details</h4>
                          <p className="text-gray-700 whitespace-pre-line">{bid.description}</p>
                        </div>
                        
                        {bid.attachments && bid.attachments.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-gray-900 mb-2">Attachments</h4>
                            <div className="flex flex-wrap gap-2">
                              {bid.attachments.map((attachment: string, index: number) => (
                                <a 
                                  key={index}
                                  href={attachment}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2 p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                                >
                                  <FileText className="h-4 w-4 text-orange-600" />
                                  <span className="text-sm text-gray-700">Attachment {index + 1}</span>
                                  <Download className="h-3 w-3 text-gray-500" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {rfq.status === 'open' && bid.status === 'pending' && (
                          <div className="flex space-x-3 mt-4">
                            <button
                              onClick={() => handleAcceptBid(bid.id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
                            >
                              <ThumbsUp className="h-4 w-4" />
                              <span>Accept Quote</span>
                            </button>
                            <button
                              onClick={() => handleRejectBid(bid.id)}
                              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
                            >
                              <ThumbsDown className="h-4 w-4" />
                              <span>Reject</span>
                            </button>
                            <button
                              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
                            >
                              <MessageCircle className="h-4 w-4" />
                              <span>Contact</span>
                            </button>
                          </div>
                        )}
                        
                        {bid.status === 'accepted' && (
                          <div className="bg-green-100 p-4 rounded-lg mt-4">
                            <div className="flex items-start space-x-3">
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="font-medium text-green-800">Quote Accepted</h4>
                                <p className="text-sm text-green-700">
                                  You've accepted this quote. Contact the supplier to proceed with the order.
                                </p>
                                <button className="mt-2 bg-white text-green-700 border border-green-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors flex items-center space-x-2">
                                  <MessageCircle className="h-4 w-4" />
                                  <span>Contact Supplier</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* RFQ Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">RFQ Status</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Status</span>
                  <div>{getStatusBadge(rfq.status)}</div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Quotes Received</span>
                  <span className="font-medium text-gray-900">{bids.length}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Expiry Date</span>
                  <span className="font-medium text-gray-900">{formatDate(rfq.expiry_date)}</span>
                </div>
                
                {rfq.delivery_deadline && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Delivery Deadline</span>
                    <span className="font-medium text-gray-900">{formatDate(rfq.delivery_deadline)}</span>
                  </div>
                )}
              </div>
              
              {rfq.status === 'open' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => navigate(`/rfq/${id}/edit`)}
                    className="w-full bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2 mb-3"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Edit RFQ</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to cancel this RFQ?')) {
                        // Handle cancel RFQ
                      }
                    }}
                    className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel RFQ
                  </button>
                </div>
              )}
            </div>
            
            {/* Buyer Information */}
            {rfq.buyers && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Buyer Information</h3>
                
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Name</span>
                    <p className="font-medium text-gray-900">{rfq.buyers.first_name} {rfq.buyers.last_name}</p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <p className="font-medium text-gray-900">{rfq.buyers.email}</p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-500">Country</span>
                    <p className="font-medium text-gray-900">{rfq.buyers.country}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Need Help */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
              <p className="text-gray-700 mb-4">
                Our sourcing experts can help you find the right suppliers and get the best quotes.
              </p>
              <button className="w-full bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2">
                <MessageCircle className="h-4 w-4" />
                <span>Contact Support</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFQDetailPage;